import {
  constFunction,
  constString,
  ListType,
  Node,
  opAnd,
  opArray,
  opArtifactAliasAlias,
  opArtifactAliases,
  opArtifactCreatedAt,
  opArtifactDescription,
  opArtifactId,
  opArtifactIsPortfolio,
  opArtifactLastMembership,
  opArtifactMembershipCreatedAt,
  opArtifactMembershipVersionIndex,
  opArtifactName,
  opArtifactProject,
  opArtifactRawTags,
  opArtifactType,
  opArtifactTypeName,
  opArtifactVersionRawTags,
  opArtifactVersions,
  opDict,
  opEntityArtifactPortfolios,
  opEntityName,
  opEntityProjects,
  opFilter,
  opFlatten,
  opMap,
  opOrgTeams,
  opProjectArtifacts,
  opProjectArtifactType,
  opProjectEntity,
  opProjectInternalId,
  opProjectName,
  opRootEntity,
  opRootOrg,
  opRootProject,
  opRootViewer,
  opSort,
  opStringIn,
  opStringLower,
  opStringNotEqual,
  opUserEntities,
  OutputNode,
} from '@wandb/weave/core';
import {useContext, useMemo} from 'react';

import {OLD_MODEL_REGISTRY_PROJECT_NAME} from '../../util/artifacts';
import {compareAsc} from '../../util/date';
import {useRampFlagAccountSelector} from '../../util/rampFeatureFlags';
import {RegistryName, RegistryNameStr} from '../../util/urls';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import {AccountType} from '../Search/SearchNav/types';

export type CollectionInfo = {
  entityName: string;
  projectName: string;
  artifactTypeName: string;
  collectionName: string;
  collectionId: string;
  description?: string;
  maxVersionIndex?: number | null;
  aliases?: string[];
};

/**
 * Return information about the collections in one or all entities.
 * Note: given an artifact type, this function returns all portfolios whose type contains
 * that artifact type substring
 */
export function useArtifactPortfoliosNode(
  artifactType: string,
  registryEntityName?: string
): OutputNode<ListType<'any'>> {
  const enableAccountSelector = useRampFlagAccountSelector();
  const {selectedAccount} = useContext(AccountSelectorContext);
  const isPersonalAccount =
    selectedAccount?.accountType === AccountType.Personal;
  const entitiesNode = useMemo(() => {
    if (
      enableAccountSelector &&
      !isPersonalAccount &&
      registryEntityName === undefined
    ) {
      return opOrgTeams({
        org: opRootOrg({orgName: constString(selectedAccount?.name ?? '')}),
      });
    }
    if (enableAccountSelector && isPersonalAccount) {
      return opRootEntity({
        entityName: constString(selectedAccount?.name ?? ''),
      });
    }
    if (registryEntityName !== undefined) {
      return opRootEntity({
        entityName: constString(registryEntityName),
      });
    }
    return opUserEntities({
      user: opRootViewer({}),
    });
  }, [
    registryEntityName,
    selectedAccount,
    isPersonalAccount,
    enableAccountSelector,
  ]);

  const portfoliosNode = useMemo(
    () =>
      opFlatten({
        arr: opEntityArtifactPortfolios({entity: entitiesNode}) as any,
      }),
    [entitiesNode]
  );

  const filteredPortfoliosNode = useMemo(
    () =>
      opFilter({
        arr: portfoliosNode,
        filterFn: constFunction({row: 'artifact'}, ({row}) => {
          return opStringIn({
            lhs: opStringLower({
              str: constString(artifactType),
            }),
            rhs: opStringLower({
              str: opArtifactTypeName({
                artifactType: opArtifactType({artifact: row}),
              }),
            }),
          });
        }),
      }),
    [artifactType, portfoliosNode]
  );

  const sortedPortfoliosNode = useMemo(
    () =>
      opSort({
        arr: filteredPortfoliosNode,
        compFn: constFunction({row: 'artifact'}, ({row}) => {
          return opArray({
            0: opArtifactMembershipCreatedAt({
              artifactMembership: opArtifactLastMembership({
                artifact: row,
              }),
            }),
          } as any);
        }),
        columnDirs: opArray({
          0: constString('desc'),
        } as any),
      }),
    [filteredPortfoliosNode]
  );

  return sortedPortfoliosNode as OutputNode<ListType<'any'>>;
}

/**
 * Return information about the collections in a specific registry project
 * of an entity
 */
export function useRegistryProjectArtifactPortfoliosNode(
  artifactType: string,
  registryEntityName: string,
  registryProjectName: string
): OutputNode<ListType<'any'>> {
  const portfoliosNode = useMemo(() => {
    const projectArtifactsNode = opProjectArtifacts({
      project: opRootProject({
        entityName: constString(registryEntityName),
        projectName: constString(registryProjectName),
      }),
    });
    return opFilter({
      arr: projectArtifactsNode as any,
      filterFn: constFunction({row: 'artifact'}, ({row}) => {
        return opArtifactIsPortfolio({
          artifact: row,
        });
      }),
    });
  }, [registryEntityName, registryProjectName]);

  const sortedPortfoliosNode = useMemo(
    () =>
      opSort({
        arr: portfoliosNode,
        compFn: constFunction({row: 'artifact'}, ({row}) => {
          return opArray({
            0: opArtifactMembershipCreatedAt({
              artifactMembership: opArtifactLastMembership({
                artifact: row,
              }),
            }),
          } as any);
        }),
        columnDirs: opArray({
          0: constString('desc'),
        } as any),
      }),
    [portfoliosNode]
  );

  return sortedPortfoliosNode as OutputNode<ListType<'any'>>;
}

/**
 * Return information about logged artifact types in one or all entities
 * @param artifactType
 * @param registryEntityName
 * @param defaultProjectName
 */
export function getArtifactTypesNode(
  artifactType: string,
  registryEntityName?: string,
  defaultProjectName?: string
): OutputNode<ListType<'any'>> {
  const entitiesNode =
    registryEntityName !== undefined
      ? opRootEntity({
          entityName: constString(registryEntityName),
        })
      : opUserEntities({
          user: opRootViewer({}),
        });

  // Exclude the default project while checking for logged artifacts
  const entityProjectsNode = opFilter({
    arr: opFlatten({
      arr: opEntityProjects({entity: entitiesNode}) as any,
    }),
    filterFn: constFunction({row: 'project'}, ({row}) => {
      return opAnd({
        lhs: opStringNotEqual({
          lhs: constString(defaultProjectName ?? ''),
          rhs: opProjectName({
            project: row,
          }),
        } as any),
        rhs: opStringNotEqual({
          lhs: constString(OLD_MODEL_REGISTRY_PROJECT_NAME),
          rhs: opProjectName({
            project: row,
          }),
        } as any),
      });
    }),
  });

  const artifactTypesNode = opProjectArtifactType({
    project: entityProjectsNode as any,
    artifactType: constString(artifactType),
  });

  return artifactTypesNode as OutputNode<ListType<'any'>>;
}

/**
 * Data dictionary for node pointing to list of artifact collections
 */
export const artifactsNodeToPathDictNode = (
  artifactsNode: Node<{type: 'list'; objectType: 'any'}>
) => {
  return opDict({
    artifactCollections: opMap({
      arr: opFlatten({
        arr: artifactsNode as any,
      }),
      mapFn: constFunction({row: 'artifact'}, ({row}) => {
        return opDict({
          id: opArtifactId({
            artifact: row,
          }),
          projectName: opProjectName({
            project: opArtifactProject({
              artifact: row,
            }),
          }),
          entityName: opEntityName({
            entity: opProjectEntity({
              project: opArtifactProject({
                artifact: row,
              }),
            }),
          }),
          projectId: opProjectInternalId({
            project: opArtifactProject({
              artifact: row,
            }),
          }),
          collectionId: opArtifactId({
            artifact: row,
          }),
          artifactTypeName: opArtifactTypeName({
            artifactType: opArtifactType({artifact: row}),
          }),
          collectionName: opArtifactName({
            artifact: row,
          }),
          rawTags: opArtifactRawTags({
            artifact: row,
          }),
          rawVersionTags: opArtifactVersionRawTags({
            artifactVersion: opArtifactVersions({artifact: row}),
          }),
          lastMembershipCreatedAt: opArtifactMembershipCreatedAt({
            artifactMembership: opArtifactLastMembership({
              artifact: row,
            }),
          }),
          createdAt: opArtifactCreatedAt({
            artifact: row,
          }),
          isPortfolio: opArtifactIsPortfolio({
            artifact: row,
          }),
        } as any);
      }),
    }),
  } as any);
};

/**
 * Utility function to sort collection array by their update dates
 * and creation dates, if the collection has no versions
 */
export const sortByArtifactMembershipDates = (
  lastCreated1: string,
  lastCreated2: string,
  lastUpdated1?: string,
  lastUpdated2?: string
) => {
  const updated1 = lastUpdated1 ? lastUpdated1 : lastCreated1;
  const updated2 = lastUpdated2 ? lastUpdated2 : lastCreated2;
  return compareAsc(new Date(updated2), new Date(updated1));
};

/**
 * Data dictionary for node pointing to list of artifact collections with basic information
 * about the artifact
 */
export const artifactsNodeToCollectionInfo = (
  artifactsNode: Node<{type: 'list'; objectType: 'any'}>
) => {
  return opDict({
    artifactCollections: opMap({
      arr: opFlatten({
        arr: artifactsNode as any,
      }),
      mapFn: constFunction({row: 'artifact'}, ({row}) => {
        return opDict({
          projectName: opProjectName({
            project: opArtifactProject({
              artifact: row,
            }),
          }),
          entityName: opEntityName({
            entity: opProjectEntity({
              project: opArtifactProject({
                artifact: row,
              }),
            }),
          }),
          artifactTypeName: opArtifactTypeName({
            artifactType: opArtifactType({
              artifact: row,
            }),
          }),
          collectionName: opArtifactName({
            artifact: row,
          }),
          collectionId: opArtifactId({
            artifact: row,
          }),
          description: opArtifactDescription({
            artifact: row,
          }),
          maxVersionIndex: opArtifactMembershipVersionIndex({
            artifactMembership: opArtifactLastMembership({
              artifact: row,
            }),
          }),
          aliases: opMap({
            arr: opArtifactAliases({
              artifact: row,
            }),
            mapFn: constFunction({row: 'artifactAlias'}, ({row: aliasRow}) => {
              return opArtifactAliasAlias({
                artifactAlias: aliasRow,
              });
            }),
          }),
        } as any);
      }),
    }),
  } as any) as OutputNode<{
    type: 'typedDict';
    propertyTypes: {
      artifactCollections: {
        type: 'list';
        objectType: {
          type: 'typedDict';
          propertyTypes: {
            collectionName: 'string';
            collectionId: 'string';
            entityName: 'string';
            projectName: 'string';
            artifactTypeName: 'string';
            description: 'string';
            maxVersionIndex: 'number';
            aliases: {
              type: 'list';
              objectType: 'string';
            };
          };
        };
      };
    };
  }>;
};

// Utility to derive registry name from artifact type name
export function getRegistryName(artifactTypeName: string): string {
  const enumVals = Object.values(RegistryNameStr) as RegistryName[];
  for (const val of enumVals) {
    if (artifactTypeName.toLowerCase().includes(val)) {
      return val;
    }
  }
  console.error('Invalid artifact type name for a collection in the registry');
  return artifactTypeName;
}

export const DEFAULT_PROJECT_NAME = 'model-registry';
export const COLLECTIONS_TAB_NAME = 'collections';
export const AUTOMATIONS_TAB_NAME = 'automations';
